import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSystem, setCheckingSystem] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Verificar si el sistema está inicializado
  useEffect(() => {
    const checkSystemInitialized = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/system-initialized`);
        const data = await response.json();
        
        if (!data.initialized) {
          navigate('/initial-setup');
        } else {
          setCheckingSystem(false);
        }
      } catch (error) {
        console.error('Error checking system initialization:', error);
        setCheckingSystem(false);
      }
    };

    checkSystemInitialized();
  }, [navigate]);

  // No renderizar nada mientras se verifica el sistema (evita parpadeo)
  if (checkingSystem) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress sx={{ color: '#00B0BD' }} />
      </Box>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión. Por favor, verifica tus credenciales.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/login_background.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'brightness(0.7)',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1, maxWidth: '350px' }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.65)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            boxShadow: '8px 8px 24px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <img
              src="/indra.svg"
              alt="Logo"
              style={{ width: '80px', height: 'auto' }}
            />
            <Typography variant="h5" sx={{ mt: 2, fontWeight: 700, color: '#001923' }}>
              VacaFlow
            </Typography>
          </Box>

          {/* Title */}
          <Typography variant="h6" sx={{ mb: 3, color: '#001923', fontWeight: 500 }}>
            Iniciar Sesión
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="Usuario"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              autoFocus
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Contraseña"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                backgroundColor: '#00B0BD',
                color: '#001923',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#004254',
                },
              }}
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </Button>
          </Box>

          {/* Footer */}
          <Typography variant="body2" sx={{ mt: 2, color: '#004254', textAlign: 'center' }}>
            Sistema de Gestión de Vacaciones
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
